// Composes stages 01-03 into draw lists — one per layer. Everything here is
// renderer-agnostic; nothing below this file knows what a pixel is.
//
// Layers exist from day one even when length is 1: chapter 01 instances its own
// interference field three times at different scale, rotation and phase, and
// retrofitting that later means rewriting which stage owns the canvas.

import { fields } from './fields/index.js'
import { substrates } from './substrates/index.js'
import { motions, motionParams } from './motion/index.js'
import { createDrawList, reset } from './drawlist.js'
import { deriveSeed } from './rng.js'
import { withDefaults } from './schema.js'
import { markParams, inkParams, frameParams, needsGradient } from './marks.js'
import { clockParams } from './clock.js'
import { pointerParams, NONE } from './modulation.js'

export function emptyLayer(overrides = {}) {
  return {
    enabled: true,
    // A layer's own clock: layerTime = t * timeScale + phaseOffset. phaseOffset
    // alone could only stagger layers, never let one run faster than another —
    // which chapter 02 needs (its satellite fields carry a time*0.2 term) and
    // chapter 28 needs (each crystal turns at its own rate).
    timeScale: 1,
    phaseOffset: 0,
    field: { type: 'interference' },
    substrate: { type: 'grid' },
    motion: {},
    mark: {},
    ink: {},
    transform: { scale: 1, x: 0, y: 0, rotation: 0 },
    ...overrides,
  }
}

export const DEFAULT_SEED = 27201
// `rotation` is where a layer sits; `spin` is how fast it turns. frame.orbit
// revolves everything together, which is right for a camera and wrong for a
// scene of separate objects — chapter 28's four crystals each carry their own
// rotationSpeed, and turning them as a group never changes their relative
// orientations.
const TRANSFORM_DEFAULTS = { scale: 1, x: 0, y: 0, rotation: 0, spin: 0 }

export function emptyPiece(overrides = {}) {
  return {
    seed: DEFAULT_SEED,
    clock: {},
    frame: {},
    pointer: {},
    layers: [emptyLayer()],
    ...overrides,
  }
}

// Fills in every default so downstream code never sees undefined.
export function normalise(spec) {
  const p = emptyPiece(spec)
  return {
    ...p,
    clock: withDefaults(clockParams, p.clock),
    frame: withDefaults(frameParams, p.frame),
    pointer: {
      // A path is a string, not a numbered param, so it is validated here
      // rather than by a descriptor — and an unknown one simply binds nothing.
      path: typeof (p.pointer && p.pointer.path) === 'string' ? p.pointer.path : NONE,
      ...withDefaults(pointerParams, p.pointer),
    },
    layers: p.layers.map((l) => {
      const layer = emptyLayer(l)
      // Optional chaining because a section can arrive as null from a
      // hand-edited fragment, and the module lookup happens before
      // withDefaults gets a chance to sanitise it.
      const field = fields[layer.field?.type] || fields.interference
      const substrate = substrates[layer.substrate?.type] || substrates.grid
      return {
        ...layer,
        field: { type: field.id, ...withDefaults(field.params, layer.field) },
        substrate: { type: substrate.id, ...withDefaults(substrate.params, layer.substrate) },
        motion: withDefaults(motionParams, layer.motion),
        // typeof, not Number(): JSON has no NaN, so an unwritable value
        // arrives as null and Number(null) is 0 — which would silently freeze
        // the layer rather than fall back. Same trap the morph duration hit.
        // Bounded because nothing useful happens past a few dozen times speed,
        // and an unbounded one aliases into noise.
        timeScale: typeof layer.timeScale === 'number' && Number.isFinite(layer.timeScale)
          ? Math.min(64, Math.max(-64, layer.timeScale)) : 1,
        // Merged, not spread: emptyLayer replaces `transform` wholesale, so a
        // partial one — from minimise dropping a zero, or from a hand-edited
        // fragment — would otherwise arrive with holes where its defaults
        // should be.
        transform: { ...TRANSFORM_DEFAULTS, ...(layer.transform && typeof layer.transform === 'object' ? layer.transform : {}) },
        mark: withDefaults(markParams, layer.mark),
        ink: withDefaults(inkParams, layer.ink),
      }
    }),
  }
}

// ── The dual of normalise ───────────────────────────────────────────────────
// normalise fills every default in; minimise takes every default back out, so
// a piece travels as only what makes it different from an empty one. Both read
// the same param descriptors, so they cannot drift apart — a new param is
// handled by both the moment it is declared.
function thin(params, cfg) {
  const out = {}
  for (const p of params) if (cfg[p.key] !== p.default) out[p.key] = cfg[p.key]
  return out
}

function thinAgainst(defaults, cfg) {
  const out = {}
  for (const k of Object.keys(defaults)) if (cfg[k] !== defaults[k]) out[k] = cfg[k]
  return out
}

const some = (o) => (Object.keys(o).length ? o : null)

export function minimise(spec) {
  const p = normalise(spec)
  const base = emptyLayer()
  const out = {}

  if (p.seed !== DEFAULT_SEED) out.seed = p.seed
  const clock = some(thin(clockParams, p.clock))
  if (clock) out.clock = clock
  const frame = some(thin(frameParams, p.frame))
  if (frame) out.frame = frame
  const pointer = thin(pointerParams, p.pointer)
  if (p.pointer.path !== NONE) pointer.path = p.pointer.path
  if (some(pointer)) out.pointer = pointer

  out.layers = p.layers.map((l) => {
    const layer = {}

    // Type is carried separately because it selects which schema applies.
    const field = thin(fields[l.field.type].params, l.field)
    if (l.field.type !== base.field.type) field.type = l.field.type
    if (some(field)) layer.field = field

    const substrate = thin(substrates[l.substrate.type].params, l.substrate)
    if (l.substrate.type !== base.substrate.type) substrate.type = l.substrate.type
    if (some(substrate)) layer.substrate = substrate

    const motion = some(thin(motionParams, l.motion))
    if (motion) layer.motion = motion
    const mark = some(thin(markParams, l.mark))
    if (mark) layer.mark = mark
    const ink = some(thin(inkParams, l.ink))
    if (ink) layer.ink = ink
    const transform = some(thinAgainst(TRANSFORM_DEFAULTS, l.transform))
    if (transform) layer.transform = transform

    if (l.enabled !== base.enabled) layer.enabled = l.enabled
    if (l.timeScale !== base.timeScale) layer.timeScale = l.timeScale
    if (l.phaseOffset !== base.phaseOffset) layer.phaseOffset = l.phaseOffset
    return layer
  })

  return out
}

export function createPiece(spec) {
  const piece = normalise(spec)

  const built = piece.layers.map((layer, i) => {
    const fieldMod = fields[layer.field.type]
    const subMod = substrates[layer.substrate.type]
    const motion = motions[subMod.motion]
    // Each layer gets its own PRNG stream, or every layer draws identically.
    const seed = deriveSeed(piece.seed, `layer:${i}`)
    return {
      layer,
      motion,
      field: fieldMod.create(layer.field, seed),
      substrate: subMod.build(layer.substrate, seed),
      list: createDrawList(4096),
    }
  })

  return {
    piece,
    // The motion model of the first layer, surfaced for the panel readout.
    get motionId() { return built[0] ? built[0].motion.id : 'resample' },
    get stateful() { return built.some((b) => b.motion.stateful) },

    update(t) {
      const out = []
      for (const b of built) {
        if (!b.layer.enabled) continue
        reset(b.list)
        b.motion.fill(
          b.list, b.substrate, b.field,
          b.motion.stateful ? b.layer.substrate : b.layer.field,
          b.layer.motion,
          t * b.layer.timeScale + b.layer.phaseOffset,
          needsGradient(b.layer.mark, b.layer.motion),
        )
        // The chain seam: the draw list stays a flat bag, and the substrate's
        // own ordering rides alongside it. A backend that only draws marks
        // ignores both fields; a path mark walks them.
        out.push({
          list: b.list, layer: b.layer,
          runs: b.substrate.runs || null,
          closed: !!b.substrate.closed,
        })
      }
      return out
    },
  }
}
